<template>
  <eg-modal
    id="confirmation-modal"
    :title="title"
    size="md"
    centered
    :show-backdrop="true"
    @close="onHidden"
  >
    <div class="confirmation-body d-flex">
      <!-- Simple text without styling -->
      <div v-if="text" class="confirm-text">
        {{ text }}
      </div>

      <!-- Informative block with infoTitle and/or infoText -->
      <div
        v-if="infoTitle || infoText || (infoTexts && infoTexts.length)"
        class="confirmation-modal-info-block confirmation-modal-info-block--info"
      >
        <div v-if="infoTitle" class="info-header">
          <eg-icon class="mr-2" icon="bulb-light"></eg-icon>
          <span class="info-title">{{ infoTitle }}</span>
        </div>
        <div v-if="infoText" class="info-text">
          {{ infoText }}
        </div>
        <div v-if="infoTexts" class="info-texts">
          <p
            v-for="(paragraph, idx) in infoTexts"
            :key="`info-text-${idx}`"
            class="info-text"
          >
            {{ paragraph }}
          </p>
        </div>
      </div>

      <!-- Warning block with warningTitle and/or warningText -->
      <div
        v-if="
          warningTitle || warningText || (warningTexts && warningTexts.length)
        "
        class="confirmation-modal-info-block"
      >
        <div v-if="warningTitle" class="warning-header">
          <eg-icon class="mr-2" icon="information"></eg-icon>
          <span class="warning-title">{{ warningTitle }}</span>
        </div>
        <div v-if="warningText" class="warning-text">
          {{ warningText }}
        </div>
        <p
          v-for="(paragraph, idx) in warningTexts"
          :key="`warning-text-${idx}`"
          class="warning-text"
        >
          {{ paragraph }}
        </p>
      </div>
    </div>
    <template #modal-footer="{ close }">
      <div class="d-flex align-items-center justify-content-end">
        <eg-button type="secondary" @click="confirmationAction"
          >{{ cancelTitle }}
        </eg-button>

        <eg-button class="ml-2" type="primary" @click="close">
          {{ okTitle }}
        </eg-button>
      </div>
    </template>
  </eg-modal>
</template>

<script>
import EgButton from "@/components/global/eg/buttons/eg_button.vue";
import EgIcon from "@/components/global/eg/eg_icon.vue";

export default {
  name: "ConfirmationModal",
  components: {
    EgButton,
    EgIcon,
  },

  props: {
    title: {
      type: String,
      default: "Are you sure you want to continue?",
    },
    text: {
      type: String,
      default: "",
    },
    okTitle: {
      type: String,
      default: "No, stay",
    },
    cancelTitle: {
      type: String,
      default: "Yes, continue",
    },
    resolve: {
      type: Function,
      default: null,
    },
    warningTitle: {
      type: String,
      default: "",
    },
    warningText: {
      type: String,
      default: "",
    },
    warningTexts: {
      type: Array,
      default: () => [],
    },
    infoTitle: {
      type: String,
      default: "",
    },
    infoText: {
      type: String,
      default: "",
    },
    infoTexts: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isConfirmed: false,
    };
  },

  methods: {
    onHidden() {
      if (!this.isConfirmed) {
        this.resolve && this.resolve(false);
      }
    },

    async confirmationAction() {
      this.isConfirmed = true;
      this.resolve && this.resolve(true);
      this.$egModal.hide("confirmation-modal");
    },
  },
};
</script>

<style lang="scss" scoped>
.confirmation-body {
  flex-direction: column;
  gap: 1.5rem;
}

.confirm-text {
  font-size: 14px;
  line-height: 20px;
  color: var(--colors-gray-700, #374151);
}

// Warning block styles
.confirmation-modal-info-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 500px;
  background: var(--color-surface-semantic-warning-light, #fff6eb);
  padding: 20px 24px;
  border-radius: var(--radius-rd-400);

  &.confirmation-modal-info-block--info {
    background: var(--color-surface-semantic-informative-light, #ecf6f9);

    .info-header {
      display: flex;
      align-items: center;

      .eg-icon {
        color: var(--colors-turquoise-700, #0f766e) !important;
        font-size: 20px;
      }

      .info-title {
        font-weight: 600;
        color: var(--colors-turquoise-700, #0f766e);
        font-size: 14px;
        line-height: 20px;
      }
    }

    .info-text {
      color: var(--colors-gray-700, #374151);
      font-size: 14px;
      line-height: 20px;
    }
  }

  .warning-header {
    display: flex;
    align-items: center;

    .eg-icon {
      color: var(--colors-orange-600, #ea580c) !important;
      font-size: 20px;
    }

    .warning-title {
      font-weight: 600;
      color: var(--colors-orange-600, #ea580c);
      font-size: 14px;
      line-height: 20px;
    }
  }

  .warning-text {
    color: var(--colors-gray-700, #374151);
    font-size: 14px;
    line-height: 20px;
  }
  .info-texts {
    margin-left: 28px;
  }
}

#confirmation-modal {
  z-index: 1057 !important;

  ::v-deep {
    .eg-modal__body {
      padding: 24px !important;
    }
  }
}
</style>
